import React, { useEffect, useState, useCallback, useMemo } from "react";
import styled from "styled-components";
import { Input, Spin, Alert, Tag, Table, Select, Tooltip, Button, message } from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  PhoneOutlined,
  InstagramOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import customClientAxios from "../customAxios";
import { AdminLeads, AdminCrmDashboard, AdminLeadsExport } from "../utils/ApiEndpoints";
import LeadDrawer from "./LeadDrawer";

// Main Category labels — kept in sync with backend services/crm/categoryMapper.js
const MAIN_CATEGORY_LABELS = {
  music: "Music",
  dance: "Dance",
  arts_crafts: "Arts & Crafts",
  performing_arts: "Performing Arts",
  fitness_sports: "Fitness & Sports",
  wellness: "Wellness",
  food_beverage: "Food & Beverage",
  education: "Education",
  community_social: "Community & Social",
  nightlife_entertainment: "Nightlife & Entertainment",
  travel_adventure: "Travel & Adventure",
  markets_retail: "Markets & Retail",
  other: "Other",
};

// ── Styled ────────────────────────────────────────────────────────────────

const Page = styled.div`
  padding: 24px 32px 64px;
  background: #f8fafc;
  min-height: 100vh;
`;

const Header = styled.div`
  margin-bottom: 20px;
  h1 {
    margin: 0 0 4px;
    font-size: 24px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.4px;
  }
  p {
    margin: 0;
    font-size: 13px;
    color: #64748b;
  }
`;

const StatsStrip = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const StatPill = styled.div`
  padding: 10px 16px;
  background: white;
  border: 1px solid ${(p) => p.$border || "#e2e8f0"};
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12.5px;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  transition: all 0.15s ease;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);

  ${(p) =>
    p.$active &&
    `
    border-color: ${p.$accent};
    background: ${p.$accent}10;
    box-shadow: 0 0 0 2px ${p.$accent}25;
  `}

  &:hover {
    ${(p) => p.$clickable && `border-color: ${p.$accent || "#cbd5e1"};`}
  }

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(p) => p.$accent || "#94a3b8"};
  }
  .label { color: #64748b; }
  .value { font-weight: 700; color: ${(p) => p.$accent || "#0f172a"}; font-size: 15px; }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;

const TableCard = styled.div`
  background: white;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
`;

const NameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  .name { font-size: 13px; font-weight: 600; color: #0f172a; }
  .sub  { font-size: 11px; color: #94a3b8; }
`;

const ContactCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 12px;
  color: #475569;
  svg { font-size: 11px; color: #94a3b8; margin-right: 4px; }
`;

const CategoryCell = styled.div`
  font-size: 12px;
  color: #334155;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// ── Stage groups for Pre-Onboard ──────────────────────────────────────────

const STAGE_GROUPS = [
  {
    id: "all",
    label: "All",
    stages: ["new", "contacted", "qualified", "negotiating", "won"],
    accent: "#475569",
  },
  {
    id: "prospect",
    label: "Prospect",
    stages: ["new", "contacted"],
    accent: "#64748b",
    description: "Captured · not yet qualified",
  },
  {
    id: "qualified",
    label: "Qualified",
    stages: ["qualified", "negotiating"],
    accent: "#a855f7",
    description: "Clicked nurture CTA · in conversation",
  },
  {
    id: "onboarded",
    label: "Onboarded",
    stages: ["won"],
    accent: "#3b82f6",
    description: "Signed up on Growezy",
  },
];

const STAGE_BADGE = {
  new:         { color: "default",  label: "New" },
  contacted:   { color: "default",  label: "Contacted" },
  qualified:   { color: "purple",   label: "Qualified" },
  negotiating: { color: "purple",   label: "Negotiating" },
  won:         { color: "blue",     label: "Onboarded" },
};

const NURTURE_BADGE = {
  not_started: { color: "default", label: "Not started" },
  scheduled:   { color: "geekblue", label: "Scheduled" },
  in_progress: { color: "orange",  label: "Active" },
  completed:   { color: "green",   label: "Done" },
  opted_out:   { color: "red",     label: "Opted out" },
};

const relativeDays = (date) => {
  if (!date) return "—";
  const diff = dayjs().diff(dayjs(date), "day");
  if (diff === 0) return "today";
  if (diff === 1) return "1d ago";
  if (diff < 30) return `${diff}d ago`;
  if (diff < 365) return `${Math.floor(diff / 30)}mo ago`;
  return `${Math.floor(diff / 365)}y ago`;
};

// ── Component ─────────────────────────────────────────────────────────────

const PreOnboardBoard = () => {
  const [leads, setLeads] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  // Export filters — also applied to the visible table so what you see is
  // what you'll get when you click Export
  const [cityFilter, setCityFilter] = useState("");
  const [mainCategoryFilter, setMainCategoryFilter] = useState("all");
  const [hasPhoneFilter, setHasPhoneFilter] = useState("all"); // all | true | false
  const [exporting, setExporting] = useState(false);
  const [activeGroup, setActiveGroup] = useState("all");
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });
  const [selectedLeadId, setSelectedLeadId] = useState(null);

  // Active stage list driven by selected group
  const activeStages = useMemo(() => {
    const g = STAGE_GROUPS.find((g) => g.id === activeGroup);
    return g ? g.stages.join(",") : "";
  }, [activeGroup]);

  // Build the query-params object once — used by both fetch + export so they
  // stay in lockstep.
  const buildFilterParams = useCallback(() => {
    const params = {};
    if (activeStages) params.stage = activeStages;
    if (search?.trim()) params.search = search.trim();
    if (sourceFilter && sourceFilter !== "all") params.source = sourceFilter;
    if (cityFilter?.trim()) params.city = cityFilter.trim();
    if (mainCategoryFilter && mainCategoryFilter !== "all") params.mainCategory = mainCategoryFilter;
    if (hasPhoneFilter !== "all") params.hasPhone = hasPhoneFilter;
    return params;
  }, [activeStages, search, sourceFilter, cityFilter, mainCategoryFilter, hasPhoneFilter]);

  const fetchPage = useCallback(async (page = 1, pageSize = 50) => {
    setLoading(true);
    setError(null);
    try {
      const params = { ...buildFilterParams(), page, limit: pageSize };
      const r = await customClientAxios().get(AdminLeads, { params });
      setLeads(r.data?.data?.leads || []);
      setPagination({
        current: r.data?.data?.pagination?.page || 1,
        pageSize,
        total: r.data?.data?.pagination?.total || 0,
      });
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams]);

  const exportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const params = buildFilterParams();
      // Use customClientAxios so auth headers / token-refresh stay consistent.
      const r = await customClientAxios().get(AdminLeadsExport, {
        params,
        responseType: "blob",
      });
      // Pull filename out of Content-Disposition; fall back to a sensible default
      const cd = r.headers["content-disposition"] || r.headers["Content-Disposition"] || "";
      const fnMatch = /filename="?([^";]+)"?/i.exec(cd);
      const filename = fnMatch ? fnMatch[1] : `growezy-contacts_${dayjs().format("YYYY-MM-DD_HH-mm")}.csv`;
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      message.success(`Exported as ${filename}`);
    } catch (e) {
      message.error(e?.response?.data?.message || e.message || "Export failed");
    } finally {
      setExporting(false);
    }
  }, [buildFilterParams]);

  const fetchFunnel = useCallback(async () => {
    try {
      const r = await customClientAxios().get(`${AdminCrmDashboard}?window=all`);
      setFunnel(r.data?.data?.funnel || null);
    } catch {/* non-fatal */}
  }, []);

  useEffect(() => { fetchFunnel(); }, [fetchFunnel]);
  useEffect(() => { fetchPage(1, pagination.pageSize); /* eslint-disable-next-line */ }, [activeStages]);

  // Sources for the filter dropdown
  const [sources, setSources] = useState([]);
  useEffect(() => {
    customClientAxios().get(AdminLeads, { params: { limit: 1000 } })
      .then((r) => {
        const set = new Set();
        (r.data?.data?.leads || []).forEach((l) => l.source && set.add(l.source));
        setSources(Array.from(set).sort());
      })
      .catch(() => {});
  }, []);

  // Totals for each stage group from the dashboard funnel (source of truth)
  const groupTotals = useMemo(() => {
    if (!funnel) return { all: 0, prospect: 0, qualified: 0, onboarded: 0 };
    return {
      prospect:  funnel.prospect || 0,
      qualified: funnel.qualified || 0,
      onboarded: funnel.onboarded || 0,
      all: (funnel.prospect || 0) + (funnel.qualified || 0) + (funnel.onboarded || 0),
    };
  }, [funnel]);

  // Table columns
  const columns = [
    {
      title: "Name",
      key: "name",
      render: (_, lead) => (
        <NameCell>
          <span className="name">{lead.name || "(no name)"}</span>
          {lead.city && <span className="sub">{lead.city}</span>}
        </NameCell>
      ),
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Contact",
      key: "contact",
      render: (_, lead) => (
        <ContactCell>
          {lead.phone && <div><PhoneOutlined /> {lead.phone}</div>}
          {lead.email && <div>✉ {lead.email}</div>}
        </ContactCell>
      ),
    },
    {
      title: "Stage",
      dataIndex: "stage",
      key: "stage",
      width: 120,
      render: (s) => {
        const b = STAGE_BADGE[s] || { color: "default", label: s };
        return <Tag color={b.color} style={{ margin: 0 }}>{b.label}</Tag>;
      },
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      width: 110,
      render: (s) => <Tag style={{ margin: 0, textTransform: "capitalize" }}>{s || "—"}</Tag>,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 220,
      render: (c) => {
        if (!c) return <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>—</span>;
        return (
          <Tooltip title={c} mouseEnterDelay={0.2}>
            <CategoryCell>{c}</CategoryCell>
          </Tooltip>
        );
      },
      sorter: (a, b) => (a.category || "").localeCompare(b.category || ""),
    },
    {
      title: "Nurture",
      dataIndex: "nurtureStatus",
      key: "nurtureStatus",
      width: 110,
      render: (s, lead) => {
        const b = NURTURE_BADGE[s] || NURTURE_BADGE.not_started;
        return (
          <span>
            <Tag color={b.color} style={{ margin: 0 }}>{b.label}</Tag>
            {lead.hasLinkedInstagram && (
              <Tag color="magenta" style={{ marginLeft: 4 }}>
                <InstagramOutlined style={{ fontSize: 10 }} /> IG
              </Tag>
            )}
          </span>
        );
      },
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 110,
      render: (d) => (
        <Tooltip title={dayjs(d).format("DD MMM YYYY HH:mm")}>
          <span style={{ fontSize: 12, color: "#64748b" }}>{relativeDays(d)}</span>
        </Tooltip>
      ),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 110,
      render: (d) => (
        <Tooltip title={dayjs(d).format("DD MMM YYYY HH:mm")}>
          <span style={{ fontSize: 12, color: "#64748b" }}>{relativeDays(d)}</span>
        </Tooltip>
      ),
      sorter: (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt),
    },
  ];

  if (error) {
    return <Page><Alert type="error" message={error} showIcon /></Page>;
  }

  return (
    <Page>
      <Header>
        <h1>Pre-Onboard Contacts</h1>
        <p>Prospect → Qualified → Onboarded · click any row for full profile</p>
      </Header>

      <StatsStrip>
        {STAGE_GROUPS.map((g) => (
          <StatPill
            key={g.id}
            $clickable
            $active={activeGroup === g.id}
            $accent={g.accent}
            onClick={() => { setActiveGroup(g.id); setPagination((p) => ({ ...p, current: 1 })); }}
          >
            <div className="dot" />
            <span className="label">{g.label}</span>
            <span className="value">{groupTotals[g.id] ?? 0}</span>
          </StatPill>
        ))}
      </StatsStrip>

      <FilterBar>
        <Input
          prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
          placeholder="Search name, phone, email, city…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={() => fetchPage(1, pagination.pageSize)}
          allowClear
          onClear={() => { setSearch(""); fetchPage(1, pagination.pageSize); }}
          style={{ width: 260 }}
        />
        <Input
          prefix={<span style={{ color: "#94a3b8" }}>📍</span>}
          placeholder="City contains…"
          value={cityFilter}
          onChange={(e) => setCityFilter(e.target.value)}
          onPressEnter={() => fetchPage(1, pagination.pageSize)}
          allowClear
          onClear={() => { setCityFilter(""); fetchPage(1, pagination.pageSize); }}
          style={{ width: 180 }}
        />
        <Select
          value={mainCategoryFilter}
          onChange={(v) => { setMainCategoryFilter(v); fetchPage(1, pagination.pageSize); }}
          style={{ width: 200 }}
          options={[
            { value: "all", label: "All categories" },
            ...Object.entries(MAIN_CATEGORY_LABELS).map(([k, v]) => ({ value: k, label: v })),
            { value: "_unmapped", label: "Unmapped (no category)" },
          ]}
        />
        <Select
          value={hasPhoneFilter}
          onChange={(v) => { setHasPhoneFilter(v); fetchPage(1, pagination.pageSize); }}
          style={{ width: 140 }}
          options={[
            { value: "all",   label: "Phone: any" },
            { value: "true",  label: "Has phone" },
            { value: "false", label: "No phone" },
          ]}
        />
        <Select
          value={sourceFilter}
          onChange={(v) => { setSourceFilter(v); fetchPage(1, pagination.pageSize); }}
          style={{ width: 150 }}
          options={[{ value: "all", label: "All sources" }, ...sources.map((s) => ({ value: s, label: s }))]}
        />
        <Tooltip title="Refresh">
          <ReloadOutlined
            onClick={() => fetchPage(pagination.current, pagination.pageSize)}
            style={{ cursor: "pointer", color: "#64748b", fontSize: 16, padding: "0 6px" }}
          />
        </Tooltip>
        <Tooltip title="Download CSV of all contacts matching the filters above (max 10k rows)">
          <Button
            icon={<DownloadOutlined />}
            onClick={exportCsv}
            loading={exporting}
            type="primary"
            ghost
          >
            Export CSV
          </Button>
        </Tooltip>
        <div style={{ marginLeft: "auto", fontSize: 12, color: "#94a3b8" }}>
          Showing {leads.length} of {pagination.total.toLocaleString()} contacts
        </div>
      </FilterBar>

      <TableCard>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={leads}
            rowKey="leadId"
            size="middle"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: [25, 50, 100, 200],
              showTotal: (t, range) => `${range[0]}-${range[1]} of ${t}`,
            }}
            onChange={(pag) => fetchPage(pag.current, pag.pageSize)}
            onRow={(rec) => ({
              onClick: () => setSelectedLeadId(rec.leadId),
              style: { cursor: "pointer" },
            })}
          />
        </Spin>
      </TableCard>

      <LeadDrawer
        leadId={selectedLeadId}
        open={!!selectedLeadId}
        onClose={() => setSelectedLeadId(null)}
        onUpdate={() => fetchPage(pagination.current, pagination.pageSize)}
      />
    </Page>
  );
};

export default PreOnboardBoard;
